export interface DegreesOfFreedomParams {
  dataType: 'sample' | 'population' | 'anova-between' | 'anova-within' | 'chi-square';
  sampleSize?: number;
  numberOfGroups?: number;
  numberOfLevels?: number;
  rows?: number;
  columns?: number;
}

export interface DegreesOfFreedomResult {
  degreesOfFreedom: number;
  formula: string;
  explanation: string;
}

/**
 * Calculates degrees of freedom based on the provided parameters
 * @param params - Parameters required for calculating degrees of freedom
 * @returns Result with degrees of freedom, formula used, and explanation
 */
export function calculateDegreesOfFreedom(params: DegreesOfFreedomParams): DegreesOfFreedomResult {
  const { dataType, sampleSize, numberOfGroups, numberOfLevels, rows, columns } = params;

  let degreesOfFreedom = 0;
  let formula = '';
  let explanation = '';

  switch (dataType) {
    case 'sample': {
      if (!sampleSize || sampleSize <= 1) {
        throw new Error('Sample size must be greater than 1');
      }
      degreesOfFreedom = sampleSize - 1;
      formula = 'n - 1';
      explanation = `The degrees of freedom for a sample is calculated as the sample size (n = ${sampleSize}) minus 1, resulting in ${degreesOfFreedom} degrees of freedom. This adjustment is made because one degree of freedom is lost when estimating the population parameter.`;
      break;
    }
    case 'population': {
      if (!sampleSize || sampleSize <= 0) {
        throw new Error('Population size must be greater than 0');
      }
      degreesOfFreedom = sampleSize;
      formula = 'N';
      explanation = `For a population, the degrees of freedom equals the population size (N = ${sampleSize}), which is ${degreesOfFreedom}. Since we're not estimating parameters from a sample, we use the full size.`;
      break;
    }
    case 'anova-between': {
      if (!numberOfGroups || numberOfGroups <= 1) {
        throw new Error('Number of groups must be greater than 1');
      }
      degreesOfFreedom = numberOfGroups - 1;
      formula = 'k - 1';
      explanation = `In a between-groups ANOVA, the degrees of freedom is calculated as the number of groups (k = ${numberOfGroups}) minus 1, resulting in ${degreesOfFreedom} degrees of freedom. This represents the variation between different groups.`;
      break;
    }
    case 'anova-within': {
      if (!numberOfGroups || numberOfGroups <= 1 || !sampleSize || sampleSize <= numberOfGroups) {
        throw new Error('Invalid input for ANOVA within-groups calculation');
      }
      degreesOfFreedom = sampleSize - numberOfGroups;
      formula = 'N - k';
      explanation = `In a within-groups ANOVA, the degrees of freedom is calculated as the total sample size (N = ${sampleSize}) minus the number of groups (k = ${numberOfGroups}), resulting in ${degreesOfFreedom} degrees of freedom. This represents the variation within groups.`;
      break;
    }
    case 'chi-square': {
      if (!rows || rows <= 1 || !columns || columns <= 1) {
        throw new Error('Rows and columns must both be greater than 1');
      }
      degreesOfFreedom = (rows - 1) * (columns - 1);
      formula = '(r - 1) × (c - 1)';
      explanation = `For a chi-square test with contingency table, the degrees of freedom is calculated as (rows - 1) × (columns - 1) = (${rows} - 1) × (${columns} - 1) = ${degreesOfFreedom}. This represents the number of cells in the table that can vary freely.`;
      break;
    }
    default: {
      throw new Error('Invalid data type for degrees of freedom calculation');
    }
  }

  return {
    degreesOfFreedom,
    formula,
    explanation
  };
} 